import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api, formatErr } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Copy, Pencil, Play, X, Download, LayoutDashboard } from "lucide-react";
import WidgetRenderer from "../components/WidgetRenderer";
import { API_BASE } from "../lib/api";

const REPORT_TYPES = [
  { key: "kpi", label: "KPI" },
  { key: "summary", label: "Summary table" },
  { key: "table", label: "Detailed table" },
  { key: "bar", label: "Bar chart" },
  { key: "line", label: "Line chart" },
  { key: "pie", label: "Pie chart" },
  { key: "donut", label: "Donut chart" },
  { key: "grouped", label: "Grouped table" },
];

export default function Reports() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [datasetFilter, setDatasetFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const [r, d] = await Promise.all([api.get("/reports"), api.get("/datasets")]);
    setReports(r.data.reports || []);
    setDatasets([...(d.data.datasets || []), ...(d.data.demo_datasets || [])]);
  };
  useEffect(() => { load(); }, []);

  const datasetName = (id) => datasets.find((d) => (d.id || `demo:${d.demo_key}`) === id)?.name || id;

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (filter && !(r.name || "").toLowerCase().includes(filter.toLowerCase())) return false;
      if (typeFilter !== "all" && r.report_type !== typeFilter) return false;
      if (datasetFilter !== "all" && r.dataset_id !== datasetFilter) return false;
      return true;
    });
  }, [reports, filter, typeFilter, datasetFilter]);

  const remove = async (id, name) => {
    if (!confirm(`Delete report "${name}"?`)) return;
    try { await api.delete(`/reports/${id}`); toast.success("Report deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const duplicate = async (id) => {
    try { await api.post(`/reports/${id}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Reports</h1>
            <p>Saved data views — preview, export, and add reports to dashboards.</p>
          </div>

          {canAtLeast(user, "analyst") && (
            <button className="erp-primary-btn" onClick={() => setShowCreate(true)} data-testid="reports-new">
              <Plus size={14} strokeWidth={1.8} /> Create report
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="card-flat p-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="label">Search</label>
              <input className="input" placeholder="Filter by name…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reports-search" />
            </div>
            <div>
              <label className="label">Type</label>
              <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="reports-type-filter">
                <option value="all">All types</option>
                {REPORT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Dataset</label>
              <select className="select" value={datasetFilter} onChange={(e) => setDatasetFilter(e.target.value)} data-testid="reports-ds-filter">
                <option value="all">All datasets</option>
                {datasets.map((d) => <option key={d.id || d.demo_key} value={d.id || `demo:${d.demo_key}`}>{d.name}</option>)}
              </select>
            </div>
          </div>

          <div className="card-flat p-4">
            <table className="tbl" data-testid="reports-table">
              <thead><tr>
                <th>Name</th><th>Type</th><th>Dataset</th><th>Owner</th><th>Updated</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`report-row-${r.id}`}>
                    <td>
                      <FileText size={14} strokeWidth={1.7} className="inline mr-1.5" />
                      <span className="font-medium">{r.name}</span>
                      <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{r.description || "—"}</div>
                    </td>
                    <td><span className="pill">{r.report_type}</span></td>
                    <td className="font-mono text-[12px]">{datasetName(r.dataset_id)}</td>
                    <td className="text-[12px]">{r.created_by_email || "—"}</td>
                    <td className="numeric text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{new Date(r.updated_at).toLocaleString()}</td>
                    <td className="text-right whitespace-nowrap">
                      <button className="btn btn-ghost" title="Preview / Run" onClick={() => setEditing({ mode: "view", report: r })} data-testid={`report-run-${r.id}`}><Play size={13} strokeWidth={1.7} /></button>
                      {canAtLeast(user, "analyst") && <button className="btn btn-ghost" title="Edit" onClick={() => setEditing({ mode: "edit", report: r })} data-testid={`report-edit-${r.id}`}><Pencil size={13} strokeWidth={1.7} /></button>}
                      {canAtLeast(user, "analyst") && <button className="btn btn-ghost" title="Duplicate" onClick={() => duplicate(r.id)}><Copy size={13} strokeWidth={1.7} /></button>}
                      {canAtLeast(user, "analyst") && <button className="btn btn-ghost" title="Delete" onClick={() => remove(r.id, r.name)} data-testid={`report-del-${r.id}`}><Trash2 size={13} strokeWidth={1.7} /></button>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>No reports match your filters. {canAtLeast(user, "analyst") && "Click 'Create report' to get started."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {showCreate && <ReportBuilderModal datasets={datasets} onClose={() => { setShowCreate(false); load(); }} />}
        {editing && <ReportBuilderModal datasets={datasets} existing={editing.report} viewOnly={editing.mode === "view"} onClose={() => { setEditing(null); load(); }} />}
      </main>
    </div>
  );
}

function ReportBuilderModal({ datasets, existing, viewOnly = false, onClose }) {
  const isEdit = !!existing && !viewOnly;
  const [form, setForm] = useState({
    name: existing?.name || "",
    description: existing?.description || "",
    report_type: existing?.report_type || "bar",
    dataset_id: existing?.dataset_id || "",
    dimension: existing?.dimension || "",
    measure: existing?.measure || "",
    aggregation: existing?.aggregation || "sum",
    filters: existing?.filters || [],
    sort: existing?.sort || { dir: "desc" },
    limit: existing?.limit || 100,
    number_format: existing?.number_format || {},
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(viewOnly);
  const [dashboards, setDashboards] = useState([]);
  const [addToDash, setAddToDash] = useState("");

  useEffect(() => { api.get("/dashboards").then((r) => setDashboards(r.data.dashboards || [])); }, []);

  const update = (p) => { setForm((s) => ({ ...s, ...p })); setError(""); setShowPreview(false); };
  const dataset = datasets.find((d) => (d.id || `demo:${d.demo_key}`) === form.dataset_id);
  const cols = dataset?.fields || [];
  const dims = cols.filter((c) => c.role === "dimension");
  const measures = cols.filter((c) => c.role === "measure");

  const save = async () => {
    setError("");
    if (!form.name?.trim()) { setError("Name is required"); return; }
    if (!form.dataset_id) { setError("Pick a dataset"); return; }
    if (!form.report_type) { setError("Pick a report type"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/reports/${existing.id}`, form);
      else await api.post("/reports", form);
      toast.success(isEdit ? "Report updated" : "Report saved");
      onClose();
    } catch (e) { const m = formatErr(e); setError(m); toast.error(m); }
    finally { setSaving(false); }
  };

  const addToDashboard = async () => {
    if (!existing || !addToDash) return;
    try { await api.post(`/reports/${existing.id}/add-to-dashboard`, { dashboard_id: addToDash }); toast.success("Added to dashboard"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const exportCsv = async () => {
    if (!existing) return;
    try {
      const token = localStorage.getItem("camaroo_token");
      const widget = {
        id: existing.id, type: form.report_type === "summary" || form.report_type === "grouped" ? "table" : form.report_type,
        title: form.name, dataset_id: form.dataset_id, dimension: form.dimension, measure: form.measure,
        aggregation: form.aggregation, filters: form.filters, sort: form.sort, limit: form.limit,
      };
      const r = await fetch(`${API_BASE}/export/widget.csv`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ widget, filters: [] }),
      });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${form.name.replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const previewWidget = {
    id: existing?.id || "preview", type: form.report_type === "summary" || form.report_type === "grouped" ? "table" : form.report_type,
    title: form.name, dataset_id: form.dataset_id, dimension: form.dimension, measure: form.measure,
    aggregation: form.aggregation, filters: form.filters, sort: form.sort, limit: form.limit,
    number_format: form.number_format,
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30" data-testid="report-modal">
      <div className="bg-white w-[920px] max-w-[97vw] max-h-[92vh] overflow-auto rounded-md border" style={{ borderColor: "var(--c-border)" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white" style={{ borderColor: "var(--c-border)" }}>
          <div>
            <div className="section-title">{viewOnly ? `Preview · ${existing?.name}` : (isEdit ? `Edit · ${existing?.name}` : "Create report")}</div>
            <div className="section-sub">Build a report against any dataset. Live data is queried as you preview.</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={14} strokeWidth={1.8} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <label className="label">Report name</label>
              <input className="input" value={form.name} onChange={(e) => update({ name: e.target.value })} disabled={viewOnly} data-testid="report-name" />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea className="input" rows="2" value={form.description} onChange={(e) => update({ description: e.target.value })} disabled={viewOnly} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Report type</label>
                <select className="select" value={form.report_type} onChange={(e) => update({ report_type: e.target.value })} disabled={viewOnly} data-testid="report-type">
                  {REPORT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Dataset</label>
                <select className="select" value={form.dataset_id} onChange={(e) => update({ dataset_id: e.target.value, dimension: "", measure: "" })} disabled={viewOnly} data-testid="report-dataset">
                  <option value="">— pick dataset —</option>
                  {datasets.map((d) => <option key={d.id || `demo:${d.demo_key}`} value={d.id || `demo:${d.demo_key}`}>{d.name}{d.is_demo ? " (demo)" : ""}</option>)}
                </select>
              </div>
            </div>
            {form.report_type !== "kpi" && (
              <div>
                <label className="label">Dimension (group/X-axis)</label>
                <select className="select" value={form.dimension} onChange={(e) => update({ dimension: e.target.value })} disabled={viewOnly} data-testid="report-dim">
                  <option value="">— select —</option>
                  {dims.map((c) => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Measure</label>
                <select className="select" value={form.measure} onChange={(e) => update({ measure: e.target.value })} disabled={viewOnly} data-testid="report-measure">
                  <option value="">— count rows —</option>
                  {[...measures, ...dims].map((c) => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Aggregation</label>
                <select className="select" value={form.aggregation} onChange={(e) => update({ aggregation: e.target.value })} disabled={viewOnly} data-testid="report-agg">
                  {["sum", "avg", "count", "count_distinct", "min", "max"].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Limit</label>
                <input className="input" type="number" value={form.limit} onChange={(e) => update({ limit: Number(e.target.value) })} disabled={viewOnly} />
              </div>
              <div>
                <label className="label">Sort</label>
                <select className="select" value={form.sort?.dir || "desc"} onChange={(e) => update({ sort: { dir: e.target.value } })} disabled={viewOnly}>
                  <option value="desc">Descending</option><option value="asc">Ascending</option>
                </select>
              </div>
              <div>
                <label className="label">Suffix</label>
                <input className="input" value={form.number_format?.suffix || ""} onChange={(e) => update({ number_format: { ...form.number_format, suffix: e.target.value } })} disabled={viewOnly} />
              </div>
            </div>
            {error && <div className="text-[12.5px] px-3 py-2 rounded" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}>{error}</div>}
            {!viewOnly && (
              <div className="flex gap-2 pt-2">
                <button className="btn btn-outline" onClick={() => setShowPreview(true)}><Play size={13} strokeWidth={1.8} /> Preview</button>
                <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="report-save">{saving ? "Saving…" : "Save report"}</button>
              </div>
            )}
            {existing && (
              <div className="pt-3 border-t" style={{ borderColor: "var(--c-border)" }}>
                <div className="kpi-label mb-2">Actions on saved report</div>
                <div className="flex gap-2 flex-wrap">
                  <button className="btn btn-outline" onClick={exportCsv} data-testid="report-export"><Download size={13} strokeWidth={1.8} /> Export CSV</button>
                  <select className="select" value={addToDash} onChange={(e) => setAddToDash(e.target.value)} style={{ width: "auto" }} data-testid="report-dash-pick">
                    <option value="">Add to dashboard…</option>
                    {dashboards.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <button className="btn btn-outline" onClick={addToDashboard} disabled={!addToDash} data-testid="report-add-dash"><LayoutDashboard size={13} strokeWidth={1.8} /> Add</button>
                </div>
              </div>
            )}
          </div>
          <div className="border rounded p-3" style={{ borderColor: "var(--c-border)", minHeight: 360 }} data-testid="report-preview">
            <div className="kpi-label mb-2">Preview</div>
            {showPreview && form.dataset_id ? (
              <div style={{ height: 380 }}><WidgetRenderer widget={previewWidget} /></div>
            ) : (
              <div className="text-[12.5px] py-12 text-center" style={{ color: "var(--c-text-muted)" }}>
                {form.dataset_id ? 'Click "Preview" to run the report.' : "Pick a dataset to start."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
